import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Sparkles, Save, Trash2, Search, MapPin, CheckCircle2, Wand2, ShieldCheck, FileText, Gauge, ShieldAlert } from "lucide-react";
import {
    patchProspect,
    deleteProspect,
    generateForProspect,
    scoreProspect,
    PROSPECT_STATUSES,
    STATUS_COLORS,
} from "@/lib/chiroApi";
import ScorePill from "@/components/ScorePill";

const FIELD_LIST = [
    { k: "practice_name", label: "Practice name", required: true },
    { k: "website", label: "Website" },
    { k: "city", label: "City" },
    { k: "state", label: "State" },
    { k: "zip", label: "ZIP" },
    { k: "phone", label: "Phone" },
    { k: "email", label: "Email" },
    { k: "gbp_link", label: "Google Business Profile" },
    { k: "contact_name", label: "Contact / owner" },
    { k: "chirotracker_report_link", label: "ChiroTracker report link" },
    { k: "last_contacted_at", label: "Last contacted (YYYY-MM-DD)" },
    { k: "next_followup_at", label: "Next follow-up (YYYY-MM-DD)" },
];

// iter28 — opportunity tier color palette (same palette as CampaignDetail).
const DETAIL_TIER_STYLES = {
    strong:   "border-[#7cc93c]/60 text-[#9be05c]",
    moderate: "border-[#f0c857]/60 text-[#f7d77a]",
    weak:     "border-[#e59866]/50 text-[#eab286]",
    no_hook:  "border-[#262a2e] text-[#6b7280]",
};

export default function ProspectDetail({ prospect, open, onOpenChange, onChanged, onDeleted }) {
    const [draft, setDraft] = useState(prospect || {});
    const [busy, setBusy] = useState(false);
    const [genBusy, setGenBusy] = useState(null);

    useEffect(() => {
        setDraft(prospect || {});
    }, [prospect]);

    if (!prospect) return null;

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            const updates = {};
            FIELD_LIST.forEach(({ k }) => {
                if ((draft[k] ?? "") !== (prospect[k] ?? "")) updates[k] = draft[k] || null;
            });
            [
                "status",
                "visibility_issue",
                "proof_angle",
                "draft_email",
                "notes",
                "google_search_url",
                "google_maps_search_url",
                "proof_context_notes",
            ].forEach((k) => {
                if ((draft[k] ?? "") !== (prospect[k] ?? "")) updates[k] = draft[k] || null;
            });
            if (Object.keys(updates).length === 0) {
                toast("No changes", { description: "Nothing to save." });
                setBusy(false);
                return;
            }
            const updated = await patchProspect(prospect.id, updates);
            toast.success("Saved");
            onChanged(updated);
        } catch {
            toast.error("Save failed");
        } finally {
            setBusy(false);
        }
    };

    const markProofChecked = async () => {
        try {
            const updated = await patchProspect(prospect.id, {
                last_proof_checked_at: new Date().toISOString(),
            });
            setDraft(updated);
            toast.success("Proof checked");
            onChanged(updated);
        } catch {
            toast.error("Update failed");
        }
    };

    const generate = async (kind) => {
        setGenBusy(kind);
        // iter28 — if this prospect is in the "no_hook" tier, confirm the
        // operator wants to override and then send allow_no_hook=true.
        let allowNoHook = false;
        if (draft.opportunity_tier === "no_hook" && !draft.disqualified) {
            const ok = window.confirm(
                "This prospect currently shows no visible outreach hook " +
                "(Opportunity < 10). This doesn't mean the practice has no " +
                "SEO problems — it means ProofCommand hasn't measured deeper " +
                "signals yet. Draft outreach anyway?"
            );
            if (!ok) {
                setGenBusy(null);
                return;
            }
            allowNoHook = true;
        }
        try {
            const updated = await generateForProspect(prospect.id, kind, {
                allow_no_hook: allowNoHook,
            });
            setDraft(updated);
            const label =
                kind === "from_notes"
                    ? "Drafted issue + angle + email from your notes"
                    : `Generated ${kind}`;
            toast.success(label);
            // Surface any per-stage warnings (e.g. AI fallback, safety-review fallback).
            (updated.warnings || []).forEach((w) => {
                toast.warning(`${w.stage}: ${w.reason}`, { duration: 8000 });
            });
            onChanged(updated);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.message) {
                // Phase 4 — friendly block message for disqualified / not_operational
                toast.error(detail.message, { duration: 8000 });
            } else {
                const reason =
                    detail ||
                    err?.response?.statusText ||
                    err?.message ||
                    "unknown error";
                toast.error(`Generation failed: ${reason}`);
            }
        } finally {
            setGenBusy(null);
        }
    };

    const rescore = async () => {
        try {
            const updated = await scoreProspect(prospect.id);
            setDraft(updated);
            toast.success(
                `Scored: Fit ${updated.fit_score} · Opp ${updated.opportunity_score}` +
                    (updated.disqualified ? " · DISQUALIFIED" : ""),
            );
            onChanged(updated);
        } catch {
            toast.error("Score failed");
        }
    };

    const draftBlocked =
        draft.disqualified ||
        (draft.google_business_status &&
            draft.google_business_status !== "OPERATIONAL");

    const remove = async () => {
        if (!window.confirm(`Delete prospect "${prospect.practice_name}"?`)) return;
        await deleteProspect(prospect.id);
        toast.success("Deleted");
        onDeleted?.(prospect.id);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!bg-[#111315] !border-l !border-[#262a2e] !p-0 !rounded-none w-full sm:!max-w-[640px] overflow-y-auto"
                data-testid="prospect-detail"
            >
                <SheetHeader className="p-6 border-b border-[#262a2e]">
                    <div className="flex items-center gap-3 mb-2">
                        <span
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] text-[10px] uppercase tracking-[0.15em] border"
                            style={{
                                color: STATUS_COLORS[draft.status] || "#9ca3af",
                                borderColor: `${STATUS_COLORS[draft.status] || "#9ca3af"}55`,
                                background: `${STATUS_COLORS[draft.status] || "#9ca3af"}15`,
                            }}
                        >
                            <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: STATUS_COLORS[draft.status] || "#9ca3af" }}
                            />
                            {draft.status}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                            prospect
                        </span>
                    </div>
                    <SheetTitle className="font-sans text-lg font-semibold text-[#f3f4f6] text-left">
                        {draft.practice_name || "(untitled)"}
                    </SheetTitle>
                    <SheetDescription asChild>
                        <div className="font-mono text-[10px] text-[#9ca3af] text-left">
                            id: {prospect.id}
                            <br />
                            updated: {draft.updated_at}
                        </div>
                    </SheetDescription>
                </SheetHeader>

                <div className="p-6 space-y-5">
                    {/* v2 Phase 4 — Score Card */}
                    <ScoreCard draft={draft} onRescore={rescore} draftBlocked={draftBlocked} />

                    {draft.safety_reviewed_at && (
                        <SafetyReviewBanner draft={draft} prospect={prospect} />
                    )}
                    {/* Basic fields */}
                    <div className="grid grid-cols-2 gap-3">
                        {FIELD_LIST.map(({ k, label }) => (
                            <Field key={k} label={label}>
                                <input
                                    value={draft[k] || ""}
                                    onChange={(e) => set(k, e.target.value)}
                                    data-testid={`detail-${k}`}
                                    className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                                />
                            </Field>
                        ))}
                        <Field label="Status">
                            <select
                                value={draft.status || "New"}
                                onChange={(e) => set("status", e.target.value)}
                                data-testid="detail-status"
                                className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                            >
                                {PROSPECT_STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    {/* Proof context — live links, not snapshots */}
                    <div className="border border-[#262a2e] bg-[#090a0b] p-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                                proof context · live links
                            </div>
                            <button
                                onClick={markProofChecked}
                                data-testid="mark-proof-checked"
                                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c] transition-colors px-2 py-1 rounded-[2px]"
                            >
                                <CheckCircle2 className="w-3 h-3" /> just checked
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <a
                                href={draft.google_search_url || "#"}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => {
                                    if (!draft.google_search_url) e.preventDefault();
                                }}
                                aria-disabled={!draft.google_search_url}
                                data-testid="open-google-search"
                                className={`inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border px-3 py-2 rounded-[2px] transition-colors ${
                                    draft.google_search_url
                                        ? "border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c]"
                                        : "border-[#1c1f22] text-[#3a3f44] cursor-not-allowed"
                                }`}
                            >
                                <Search className="w-3 h-3" /> open google search
                            </a>
                            <a
                                href={draft.google_maps_search_url || "#"}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => {
                                    if (!draft.google_maps_search_url) e.preventDefault();
                                }}
                                aria-disabled={!draft.google_maps_search_url}
                                data-testid="open-google-maps"
                                className={`inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border px-3 py-2 rounded-[2px] transition-colors ${
                                    draft.google_maps_search_url
                                        ? "border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c]"
                                        : "border-[#1c1f22] text-[#3a3f44] cursor-not-allowed"
                                }`}
                            >
                                <MapPin className="w-3 h-3" /> open google maps search
                            </a>
                        </div>

                        <Field label="Search URL (auto-filled — editable)">
                            <input
                                value={draft.google_search_url || ""}
                                onChange={(e) => set("google_search_url", e.target.value)}
                                data-testid="detail-google-search-url"
                                className="w-full bg-[#111315] border border-[#262a2e] font-mono text-[11px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                            />
                        </Field>
                        <div className="h-2" />
                        <Field label="Maps URL (auto-filled — editable)">
                            <input
                                value={draft.google_maps_search_url || ""}
                                onChange={(e) => set("google_maps_search_url", e.target.value)}
                                data-testid="detail-google-maps-url"
                                className="w-full bg-[#111315] border border-[#262a2e] font-mono text-[11px] px-3 py-2 focus:border-[#7cc93c] outline-none"
                            />
                        </Field>
                        <div className="h-2" />
                        <Field label="Proof context notes">
                            <textarea
                                value={draft.proof_context_notes || ""}
                                onChange={(e) => set("proof_context_notes", e.target.value)}
                                data-testid="detail-proof-context-notes"
                                rows={3}
                                placeholder="What did the SERP / map pack actually show?"
                                className="w-full bg-[#111315] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
                            />
                            <div className="mt-1.5 font-mono text-[10px] text-[#9ca3af] leading-relaxed">
                                Write 1–3 plain-English observations from the live search/map review.
                                The AI will use these to generate a grounded issue and outreach draft.
                            </div>
                        </Field>
                        <div className="mt-3">
                            <button
                                onClick={() => generate("from_notes")}
                                disabled={genBusy === "from_notes"}
                                data-testid="generate-from-notes"
                                className="w-full inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-2 rounded-[2px] disabled:opacity-50"
                            >
                                <Wand2 className="w-3 h-3" />
                                {genBusy === "from_notes"
                                    ? "Drafting from notes…"
                                    : "Generate issue + angle + email from proof notes"}
                            </button>
                        </div>
                        {draft.last_proof_checked_at && (
                            <div className="mt-2 font-mono text-[10px] text-[#9ca3af]">
                                last checked: {draft.last_proof_checked_at.replace("T", " ").slice(0, 19)}Z
                            </div>
                        )}
                    </div>

                    {/* Generate sections */}
                    <GenSection
                        label="Visibility issue found"
                        value={draft.visibility_issue}
                        onChange={(v) => set("visibility_issue", v)}
                        onGenerate={() => generate("issue")}
                        busy={genBusy === "issue"}
                        testid="detail-visibility-issue"
                        kind="issue"
                    />
                    <GenSection
                        label="Strongest proof angle"
                        value={draft.proof_angle}
                        onChange={(v) => set("proof_angle", v)}
                        onGenerate={() => generate("angle")}
                        busy={genBusy === "angle"}
                        testid="detail-proof-angle"
                        kind="angle"
                    />
                    <GenSection
                        label="Draft outreach email"
                        value={draft.draft_email}
                        onChange={(v) => set("draft_email", v)}
                        onGenerate={() => generate("email")}
                        busy={genBusy === "email"}
                        testid="detail-draft-email"
                        kind="email"
                        rows={9}
                    />

                    <Field label="Follow-up notes">
                        <textarea
                            value={draft.notes || ""}
                            onChange={(e) => set("notes", e.target.value)}
                            data-testid="detail-notes"
                            rows={5}
                            className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
                        />
                    </Field>

                    <div className="flex items-center justify-between pt-4 border-t border-[#262a2e]">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={remove}
                                data-testid="detail-delete"
                                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#ff453a] hover:text-[#ff453a] transition-colors px-3 py-1.5 rounded-[2px]"
                            >
                                <Trash2 className="w-3 h-3" />
                                Delete
                            </button>
                            <a
                                href={`/prospects/${prospect.id}/receipt`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="view-internal-receipt"
                                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c] transition-colors px-3 py-1.5 rounded-[2px]"
                            >
                                <FileText className="w-3 h-3" />
                                View internal receipt
                            </a>
                        </div>
                        <button
                            onClick={save}
                            disabled={busy}
                            data-testid="detail-save"
                            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border border-[#7cc93c] text-[#7cc93c] hover:bg-[#7cc93c] hover:text-black transition-colors px-4 py-1.5 rounded-[2px] disabled:opacity-50"
                        >
                            <Save className="w-3 h-3" />
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                    </div>

                    <div className="font-mono text-[10px] text-[#9ca3af] text-center pt-2">
                        no emails are sent automatically. you control everything.
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function GenSection({ label, value, onChange, onGenerate, busy, testid, kind, rows = 5 }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af]">
                    {label}
                </div>
                <button
                    onClick={onGenerate}
                    disabled={busy}
                    data-testid={`generate-${kind}`}
                    className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-[#262a2e] text-[#9ca3af] hover:border-[#7cc93c] hover:text-[#7cc93c] transition-colors px-2 py-1 rounded-[2px] disabled:opacity-50"
                >
                    <Sparkles className="w-3 h-3" />
                    {busy ? "generating…" : "generate with AI"}
                </button>
            </div>
            <textarea
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                rows={rows}
                placeholder="(paste or generate)"
                className="w-full bg-[#090a0b] border border-[#262a2e] font-mono text-[12px] px-3 py-2 focus:border-[#7cc93c] outline-none resize-none"
            />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9ca3af] mb-1.5">
                {label}
            </div>
            {children}
        </div>
    );
}

function SafetyReviewBanner({ draft, prospect }) {
    const [showNotes, setShowNotes] = React.useState(false);

    const wasEdited = (
        (draft.original_generated_visibility_issue ?? "") !== (draft.visibility_issue ?? "") ||
        (draft.original_generated_proof_angle ?? "") !== (draft.proof_angle ?? "") ||
        (draft.original_generated_draft_email ?? "") !== (draft.draft_email ?? "")
    );

    const ts = (draft.safety_reviewed_at || "").replace("T", " ").slice(0, 19);

    return (
        <div
            className="border border-[#7cc93c]/30 bg-[#7cc93c]/5 px-4 py-3"
            data-testid="safety-review-banner"
        >
            <div className="flex items-center flex-wrap gap-3">
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#7cc93c]">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Safety reviewed against proof notes
                </span>
                {wasEdited && (
                    <span
                        data-testid="safety-edited-note"
                        className="inline-flex items-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff9f0a] border border-[#ff9f0a]/30 bg-[#ff9f0a]/10 px-2 py-0.5 rounded-[2px]"
                    >
                        Edited for claim safety
                    </span>
                )}
                <span className="font-mono text-[10px] text-[#9ca3af] ml-auto">{ts}Z</span>
                {draft.safety_review_notes && (
                    <button
                        onClick={() => setShowNotes((s) => !s)}
                        data-testid="safety-toggle-notes"
                        className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] hover:text-[#f3f4f6]"
                    >
                        {showNotes ? "hide" : "view"} review notes
                    </button>
                )}
            </div>
            {showNotes && draft.safety_review_notes && (
                <pre
                    data-testid="safety-review-notes"
                    className="mt-3 bg-[#090a0b] border border-[#262a2e] p-3 font-mono text-[11px] text-[#f3f4f6] whitespace-pre-wrap break-words max-h-48 overflow-auto"
                >
                    {draft.safety_review_notes}
                </pre>
            )}
        </div>
    );
}


function ScoreCard({ draft, onRescore, draftBlocked }) {
    const [showReasons, setShowReasons] = useState(false);
    const fitR = draft.fit_reasons || [];
    const oppR = draft.opportunity_reasons || [];
    const hasScored = draft.scored_at != null;

    return (
        <div
            className={`border rounded-[2px] p-4 ${
                draftBlocked
                    ? "border-[#ff453a]/40 bg-[#ff453a]/5"
                    : "border-[#262a2e] bg-[#090a0b]"
            }`}
            data-testid="prospect-score-card"
        >
            <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af]">
                    <Gauge className="w-3.5 h-3.5" />
                    Deterministic score · v{draft.score_version || "—"}
                </span>
                <ScorePill label="Fit" value={draft.fit_score} testid="detail-score-fit" />
                <ScorePill
                    label="Opp"
                    value={draft.opportunity_score}
                    testid="detail-score-opp"
                />
                {draft.opportunity_tier_label && (
                    <span
                        data-testid="detail-opp-tier-label"
                        className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-[2px] border rounded-[2px] ${
                            DETAIL_TIER_STYLES[draft.opportunity_tier] ||
                            DETAIL_TIER_STYLES.no_hook
                        }`}
                    >
                        {draft.opportunity_tier_label}
                    </span>
                )}
                {draft.disqualified && (
                    <span
                        className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#ff453a] border border-[#ff453a]/40 px-1.5 py-[1px] rounded-[2px]"
                        data-testid="detail-disqualified-pill"
                    >
                        DISQUALIFIED
                    </span>
                )}
                {draft.google_business_status &&
                    draft.google_business_status !== "OPERATIONAL" && (
                        <span
                            className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#ff453a] border border-[#ff453a]/40 px-1.5 py-[1px] rounded-[2px]"
                            data-testid="detail-not-operational-pill"
                        >
                            {draft.google_business_status}
                        </span>
                    )}
                <button
                    onClick={onRescore}
                    data-testid="rescore-btn"
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] hover:text-[#7cc93c] ml-auto border border-[#262a2e] hover:border-[#7cc93c] px-2 py-1 rounded-[2px]"
                >
                    Re-score
                </button>
            </div>

            {draft.top_observation && (
                <div
                    className="mt-3 border border-[#7cc93c]/30 bg-[#7cc93c]/5 p-3 rounded-[2px]"
                    data-testid="detail-top-observation"
                >
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#7cc93c] mb-1">
                        Top observation (deterministic priority walk)
                    </div>
                    <div className="text-[12px] text-[#f3f4f6]">{draft.top_observation}</div>
                </div>
            )}

            {/* iter28 — "No visible hook" explanation, surfaced ONLY when the
                tier is no_hook AND the prospect is otherwise clean (not
                disqualified, operational, safety-reviewable). Calm, hedged,
                honest about what hasn't been measured yet. */}
            {draft.opportunity_tier === "no_hook" &&
                !draft.disqualified &&
                (!draft.google_business_status || draft.google_business_status === "OPERATIONAL") && (
                    <div
                        className="mt-3 border border-[#262a2e] bg-[#0a0b0d] p-3 rounded-[2px]"
                        data-testid="detail-no-hook-banner"
                    >
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1">
                            No visible outreach hook
                        </div>
                        <div className="text-[12px] text-[#9ca3af] leading-relaxed">
                            No visible outreach hook found from currently available data.
                            This does <span className="text-[#f3f4f6]">not</span> mean the
                            practice has no SEO problems — it means ProofCommand has not yet
                            measured deeper signals like Maps rank, page-load speed,
                            competitor review gaps, or NAP consistency.
                        </div>
                    </div>
                )}

            {draftBlocked && (
                <div
                    className="mt-3 flex items-start gap-2 text-[12px] text-[#ff453a]"
                    data-testid="detail-draft-blocked-banner"
                >
                    <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                        AI draft generation is blocked for this prospect (
                        {draft.disqualified ? "disqualified" : "not OPERATIONAL"}). No outreach
                        will be drafted or sent.
                    </span>
                </div>
            )}

            {hasScored && (
                <button
                    onClick={() => setShowReasons((s) => !s)}
                    data-testid="toggle-score-reasons"
                    className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9ca3af] hover:text-[#f3f4f6]"
                >
                    {showReasons ? "hide" : "show"} reasons ({fitR.length + oppR.length})
                </button>
            )}

            {showReasons && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1">
                            Fit reasons
                        </div>
                        <ul
                            data-testid="detail-fit-reasons"
                            className="text-[11px] text-[#d1d5db] space-y-1 list-disc pl-4"
                        >
                            {fitR.map((r, i) => (
                                <li key={i}>{r}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9ca3af] mb-1">
                            Opportunity reasons
                        </div>
                        <ul
                            data-testid="detail-opp-reasons"
                            className="text-[11px] text-[#d1d5db] space-y-1 list-disc pl-4"
                        >
                            {oppR.map((r, i) => (
                                <li key={i}>{r}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {!hasScored && (
                <div className="mt-2 font-mono text-[11px] text-[#6b7280]">
                    Not yet scored. Click "Re-score" to compute Fit + Opportunity from existing fields.
                </div>
            )}
        </div>
    );
}
